/**
 * ことばの設定(海外展開の土台。docs/10_paltown_platform.md §1「海外展開を見据えた」)。
 *
 * ここが「どの言語を提供しているか」の単一ソース。
 *   - 第一言語(firstLanguage) = その子の母語 = UI・訳の表示に使う言語
 *   - 学びたい言語(learningLanguages) = タイピングで練習する言語(複数可)
 *
 * 言語を増やすときは `LANGUAGES` に1行足すだけで、
 * 設定UI・モード選択の並び・訳のフォールバックがすべて追随する。
 *
 * TODO(i18n): UI文言そのものの多言語化は未対応(いまは日本語のみ)。
 *   `LanguageOption.label` 等を将来 i18n 辞書のキーに差し替える。
 */

import type { Language } from './content';

/** 言語コード(BCP-47 の primary subtag)。将来 'ko' | 'zh' 等を足す。 */
export type LanguageCode = 'ja' | 'en';

export interface LanguageOption {
  readonly code: LanguageCode;
  /** その言語の自称(第一言語えらびで出す。例: にほんご / English)。 */
  readonly label: string;
  /**
   * タイピングのモード(`services/content` の Language)。
   * まだステージを用意していない言語は `null`(設定には出るがモードボタンは出さない)。
   */
  readonly mode: Language | null;
  /** モードボタンの表示(ひらがな優先。小学生向け)。 */
  readonly modeName: string;
  readonly modeEmoji: string;
  readonly modeDesc: string;
  /** モードボタンの配色クラス(styles.css の `.mode-card.ja` / `.mode-card.en`)。 */
  readonly modeClass: string;
}

/**
 * 提供言語(この配列の順が「正規の並び順」)。
 * 将来の追加例:
 *   { code: 'ko', label: '한국어', mode: null, ... } — モードが出来るまで mode: null
 */
export const LANGUAGES: readonly LanguageOption[] = [
  {
    code: 'ja',
    label: 'にほんご',
    mode: 'japanese',
    modeName: 'にほんご',
    modeEmoji: 'あ',
    modeDesc: 'ローマ字で ひらがなを うつ',
    modeClass: 'ja',
  },
  {
    code: 'en',
    label: 'English',
    mode: 'english',
    modeName: 'えいご',
    modeEmoji: 'A',
    modeDesc: 'えいけん5きゅうの たんご',
    modeClass: 'en',
  },
];

export const DEFAULT_FIRST_LANGUAGE: LanguageCode = 'ja';

/** 日本語の言語コード(「もじの ひょうじ」を出すかの判定に使う)。 */
export const JAPANESE_LANGUAGE: LanguageCode = 'ja';

/**
 * その子の ことばの設定に**日本語が入っているか**。
 *
 * 「もじの ひょうじ(ひらがな / 漢字)」は日本語の表記の話なので、
 * 日本語を まったく使わない子には出さない(設定画面を無関係な項目で埋めない)。
 * 第一言語が日本語でも、学びたい言語に日本語を入れていても対象。
 */
export function usesJapanese(first: LanguageCode, learning: readonly LanguageCode[] = []): boolean {
  return first === JAPANESE_LANGUAGE || learning.includes(JAPANESE_LANGUAGE);
}

const CODES: readonly string[] = LANGUAGES.map((language) => language.code);

export function isLanguageCode(value: unknown): value is LanguageCode {
  return typeof value === 'string' && CODES.includes(value);
}

export function languageOption(code: LanguageCode): LanguageOption | null {
  return LANGUAGES.find((language) => language.code === code) ?? null;
}

/** 第一言語えらびの選択肢(いまは全提供言語)。 */
export function firstLanguageOptions(): readonly LanguageOption[] {
  return LANGUAGES;
}

/** 学びたい言語の選択肢(= 第一言語いがいの提供言語)。 */
export function learningLanguageOptions(first: LanguageCode): readonly LanguageOption[] {
  return LANGUAGES.filter((language) => language.code !== first);
}

/**
 * 学びたい言語の既定値。
 * 「第一言語いがいの最初の提供言語」= 日本語ユーザーなら English、
 * English ユーザーなら にほんご。
 */
export function defaultLearningLanguages(first: LanguageCode): readonly LanguageCode[] {
  const other = learningLanguageOptions(first)[0];
  return other ? [other.code] : [];
}

/**
 * 学びたい言語を正規化する(整合ガード)。
 *   - 提供していないコードを落とす
 *   - 重複を落とす
 *   - **第一言語は学びたい言語から自動で除外**(第一言語のモードは常に出るため)
 *   - 並びは `LANGUAGES` の順に揃える(表示順を安定させる)
 *
 * すべて消えた場合は `fallbackToDefault` が true のときだけ既定値に戻す
 * (ユーザーが自分でチェックを全部外した状態は尊重する)。
 */
export function normalizeLearningLanguages(
  first: LanguageCode,
  learning: readonly unknown[],
  fallbackToDefault = false,
): readonly LanguageCode[] {
  const chosen = new Set(learning.filter(isLanguageCode));
  chosen.delete(first);
  const ordered = LANGUAGES.filter((language) => chosen.has(language.code)).map(
    (language) => language.code,
  );
  if (ordered.length === 0 && fallbackToDefault) return defaultLearningLanguages(first);
  return ordered;
}

/**
 * 「どっちで あそぶ?」に出すモードの並び(docs/02_game_design.md §1)。
 *   1. 第一言語(常に出す)
 *   2. 学びたい言語(`LANGUAGES` の順)
 * ステージがまだ無い言語(mode === null)は出さない。
 */
export function modeOptions(
  first: LanguageCode,
  learning: readonly LanguageCode[],
): readonly LanguageOption[] {
  const codes = [first, ...normalizeLearningLanguages(first, learning)];
  const options: LanguageOption[] = [];
  for (const code of codes) {
    const option = languageOption(code);
    if (option && option.mode !== null) options.push(option);
  }
  return options;
}
